/* eslint-env browser, node */
/*
 * Activity JSON rules shared by the node scripts (validator CLI, corpus audit, tests) and the authoring tool.
 * Pure JavaScript, no Node APIs: everything here can run in the browser against the player-checklist JSON that
 * transformFromChecklistEntities produces. The rules are the ones docs/activity-json/AUTHORING_GUIDE.md states.
 */
const ROOT_TYPE = "checklist";
const SCOPES = ["checklist", "list", "section", "itemContainer", "item"];

// Authoring hierarchy: which node types may appear under which parent. Lists hold sections only; sections hold
// items and item containers; item containers hold leaf items; leaf items never have children. Items and sections
// may also sit directly under the activity. (assistant-context/overrides.json states the same rules.)
const HIERARCHY = {
	checklist: ["list", "section", "itemContainer", "item"],
	list: ["section"],
	section: ["itemContainer", "item"],
	itemContainer: ["item"],
	item: []
};

const LEAF_ITEM_TYPES = [
	"item", "itemLabelOnly", "itemYesNo", "itemPicker", "itemTextInput", "itemTextInputMultiline", "itemPrivateTextInput",
	"itemPrivateTextInputMultiline", "itemBarcodeScanner", "itemChoiceSingle", "itemChoiceMulti", "itemDate", "itemTime",
	"itemDateTime", "itemWeather", "itemImagePicker", "itemSketchPad", "itemRanking", "itemAddress", "itemLocation",
	"itemSelect", "itemSearch", "itemButton"
];

function scopeOf(type) {
	if (!type || type === ROOT_TYPE) {
		return "checklist";
	}
	if (type === "list" || type === "section" || type === "itemContainer") {
		return type;
	}
	return type.startsWith("item") ? "item" : null;
}

// Keys that only exist once a session has been played or a host (Cloud, player, indexer) has added them. They are
// never exported by the authoring tool. An agent authoring a template must not set them.
const RUNTIME_ROOT_KEYS = [
	"activityInstanceLaunch", "activityResult", "completionState", "displayName", "estimateApproval",
	"estimateApprovals", "estimates", "firstLevel", "followUpContainers", "followUps", "followUpsMap", "hasNext",
	"hasPrevious", "history", "historyGeolocation", "instanceId", "invoiceItems", "jobDetails", "jobId", "jobIdString",
	"overrides", "pricingContractProto", "pricingMode", "pricingSnapshotId", "progressCount", "progressTotal",
	"projectId", "relatedItemsMap", "selectedValue", "sessionName", "totalPrice", "totalPriceString", "variableGroups",
	"variables", "workflowStates", "workflowStatus", "workflowSubStatus"
];

const RUNTIME_NODE_KEYS = ["expanded", "progressCount", "progressTotal", "value", "valueUrls", "selected", "mediaAttachments", "mediaAttachmentsUrls", "isStartOfGroup", "isEndOfGroup", "label1Source"];

// ---- semantic validation (beyond the schema) ---------------------------------------------------------------------
function validateActivity(activity, options = {}) {
	const problems = [];
	const guids = new Map();
	const ids = new Map();
	const add = (level, path, message) => problems.push({ level, path, message });
	if (!activity || typeof activity !== "object" || Array.isArray(activity)) {
		add("error", "$", "The activity must be a JSON object.");
		return problems;
	}
	if (activity.type !== ROOT_TYPE) {
		add("error", "$.type", `The root node must have type "${ROOT_TYPE}".`);
	}
	RUNTIME_ROOT_KEYS.forEach(key => {
		if (Object.prototype.hasOwnProperty.call(activity, key)) {
			add("warning", `$.${key}`, "Runtime field on a template: the player or a host sets this; remove it from authored JSON.");
		}
	});
	const walk = (node, parentType, pathLabel) => {
		if (!node || typeof node !== "object" || Array.isArray(node)) {
			add("error", pathLabel, "Node must be an object.");
			return;
		}
		const type = parentType === null ? ROOT_TYPE : node.type;
		const scope = scopeOf(type);
		if (parentType !== null) {
			if (typeof type !== "string" || !scope || (scope === "item" && !leafItemTypes.includes(type))) {
				add("error", `${pathLabel}.type`, `Unknown node type ${JSON.stringify(type)}.`);
				return;
			}
			const allowed = HIERARCHY[scopeOf(parentType)] || [];
			if (!allowed.includes(scope)) {
				add("error", pathLabel, `A ${type} cannot be a child of a ${parentType}; allowed here: ${allowed.join(", ") || "nothing"}.`);
			}
		}
		if (typeof node.guid !== "string" || node.guid.trim() === "") {
			add("error", `${pathLabel}.guid`, "Every node needs a non-empty guid (a UUID string).");
		} else if (guids.has(node.guid)) {
			add("error", `${pathLabel}.guid`, `Duplicate guid ${node.guid} (first used at ${guids.get(node.guid)}).`);
		} else {
			guids.set(node.guid, pathLabel);
		}
		if (typeof node.id === "string" && node.id.trim() !== "" && parentType !== null) {
			if (ids.has(node.id)) {
				add("warning", `${pathLabel}.id`, `Duplicate id "${node.id}" (first used at ${ids.get(node.id)}); conditions, scripts and integrations address nodes by id, so keep ids unique.`);
			} else {
				ids.set(node.id, pathLabel);
			}
			if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(node.id)) {
				add("warning", `${pathLabel}.id`, `id "${node.id}" should start with a letter and use only letters, digits, "_" or "-" so scripts and conditions can reference it.`);
			}
		}
		if (scope === "checklist" || scope === "list" || scope === "section") {
			if (typeof node.name !== "string") {
				add("error", `${pathLabel}.name`, `A ${type} needs a name (a string, which may be empty for a hidden single section).`);
			} else if (node.name.trim() === "" && parentType !== null) {
				add("warning", `${pathLabel}.name`, `This ${type} has an empty name; the player shows nothing for it. Fine for a single hidden section, otherwise give it a name.`);
			} else if (node.name.trim() === "") {
				add("error", `${pathLabel}.name`, "The activity needs a name.");
			}
		} else if (typeof node.label1 !== "string" || (node.label1.trim() === "" && !node.labelOnly && type !== "itemButton")) {
			add("warning", `${pathLabel}.label1`, `A ${type} normally has a label1 (the text the user sees); exports allow it empty only inside a form container.`);
		}
		const hasOptions = key => Array.isArray(node[key]) && node[key].length > 0;
		const hasDataBinding = Boolean(node.pickerItemsDatasource || node.choiceItemsDatasource || node.dataSource || node.dataSourceId
			|| (node.authorProperties && (node.authorProperties.skuFilterMode || node.authorProperties.useSkuFilter || node.authorProperties.filter)));
		if (type === "itemPicker" && !hasOptions("pickerItems") && !hasDataBinding) {
			add("warning", `${pathLabel}.pickerItems`, "An itemPicker needs pickerItems (label/value pairs) or a pickerItemsDatasource binding.");
		}
		if ((type === "itemChoiceSingle" || type === "itemChoiceMulti") && !hasOptions("choiceItems") && !hasDataBinding) {
			add("warning", `${pathLabel}.choiceItems`, `An ${type} needs choiceItems (label/value pairs) or a data source binding.`);
		}
		if (hasOptions("pickerItems") && (type === "itemChoiceSingle" || type === "itemChoiceMulti")) {
			add("warning", `${pathLabel}.pickerItems`, `${type} reads its options from choiceItems, not pickerItems.`);
		}
		RUNTIME_NODE_KEYS.forEach(key => {
			if (Object.prototype.hasOwnProperty.call(node, key) && parentType !== null) {
				add("warning", `${pathLabel}.${key}`, "Runtime field on a template node: the player sets this; remove it from authored JSON.");
			}
		});
		const children = node.children;
		if (children !== undefined && !Array.isArray(children)) {
			add("error", `${pathLabel}.children`, "children must be an array.");
		} else if (scope === "item" && Array.isArray(children) && children.length > 0) {
			add("error", `${pathLabel}.children`, `A ${type} is a leaf item and cannot have children.`);
		} else if (Array.isArray(children)) {
			children.forEach((child, index) => walk(child, type, `${pathLabel}.children[${index}]`));
		}
	};
	const leafItemTypes = Array.isArray(options.leafItemTypes) && options.leafItemTypes.length ? options.leafItemTypes : LEAF_ITEM_TYPES;
	walk(activity, null, "$");
	crossCheck(activity, ids, guids, add);
	return problems;
}

const WEB_UNSUPPORTED_ITEM_TYPES = ["itemSelect", "itemSearch", "itemButton"];
const LINK_ID_KEYS = ["linkId", "yesNoYesLinkId", "yesNoNoLinkId", "yesNoNaLinkId"];

// References can only be checked once every id is known: condition operands, link targets and id prefixes.
function crossCheck(activity, ids, guids, add) {
	const idList = Array.from(ids.keys());
	// "<id>_N" copies, indexed by base id once, so the per-node collision check stays linear on large activities.
	const copyOf = new Map();
	idList.forEach(other => {
		const match = /^(.*)_\d+$/.exec(other);
		if (match && match[1] && !copyOf.has(match[1])) {
			copyOf.set(match[1], other);
		}
	});
	const knownRef = ref => {
		const base = ref.replace(/__(checked|notApplicable|obj|[A-Za-z0-9]+)$/, "");
		return ids.has(ref) || ids.has(base) || ref === "completionState";
	};
	const walkConditions = (value, pathLabel) => {
		if (Array.isArray(value)) {
			value.forEach((entry, index) => walkConditions(entry, `${pathLabel}[${index}]`));
			return;
		}
		if (!value || typeof value !== "object") {
			return;
		}
		if (value.type === "idRef" && typeof value.value === "string" && value.value && !knownRef(value.value)) {
			add("warning", pathLabel, `Condition references "${value.value}", which is no node id in this activity. That is fine only if a script sets it with af.setActivityVar or it arrives as a launch variable; otherwise the condition never passes.`);
		}
		if (value.type === "ifttt" && value.ifThis && value.thenThat && value.thenThat.sourceId !== value.ifThis.id) {
			add("warning", pathLabel, "Condition thenThat.sourceId must equal ifThis.id or the rule never fires.");
		}
		Object.keys(value).forEach(key => walkConditions(value[key], `${pathLabel}.${key}`));
	};
	const walkNodes = (node, parentType, pathLabel) => {
		if (!node || typeof node !== "object") {
			return;
		}
		const type = parentType === null ? ROOT_TYPE : node.type;
		if (node.conditionalVisible && typeof node.conditionalVisible === "object") {
			walkConditions(node.conditionalVisible, `${pathLabel}.conditionalVisible`);
		}
		if (parentType !== null) {
			if (WEB_UNSUPPORTED_ITEM_TYPES.includes(type)) {
				add("warning", `${pathLabel}.type`, `${type} has no renderer in the web player; it shows an empty card and still counts toward completion.`);
			}
			if (node.inputType === "number") {
				add("warning", `${pathLabel}.inputType`, "The player's numeric stepper is inputType \"numeric\" (with inputMin/inputMax/inputStep/inputPrecision); \"number\" falls through to a plain HTML input.");
			}
			if (node.enabled === false) {
				add("warning", `${pathLabel}.enabled`, "enabled:false is not durable: the player recomputes enabled from mandatory items; use conditionalVisible to hide an item instead.");
			}
			if (node.checked === true) {
				add("warning", `${pathLabel}.checked`, "A template item should start unchecked (checked:false).");
			}
			LINK_ID_KEYS.forEach(key => {
				const target = typeof node[key] === "string" ? node[key].trim() : "";
				if (!target) {
					return;
				}
				if (!target.startsWith("#")) {
					add("warning", `${pathLabel}.${key}`, `Link target "${target}" has no "#": the player treats it as another activity's checklistId. Use "#<id>" for a node in this activity.`);
				} else {
					const ref = target.slice(1).split(";")[0].split("?")[0];
					if (ref && !ids.has(ref) && !guids.has(ref)) {
						add("warning", `${pathLabel}.${key}`, `Link target "${target}" matches no node id or guid in this activity.`);
					}
				}
			});
			if (typeof node.id === "string" && node.id) {
				const clash = copyOf.get(node.id);
				if (clash && clash !== node.id) {
					add("warning", `${pathLabel}.id`, `id "${node.id}" collides with "${clash}": repeated copies of "${node.id}" are addressed as "${node.id}_N", so scripts and links may resolve to the wrong node.`);
				}
			}
		}
		(Array.isArray(node.children) ? node.children : []).forEach((child, index) => walkNodes(child, type, `${pathLabel}.children[${index}]`));
	};
	walkNodes(activity, null, "$");
}


module.exports = { ROOT_TYPE, SCOPES, HIERARCHY, LEAF_ITEM_TYPES, RUNTIME_ROOT_KEYS, RUNTIME_NODE_KEYS, WEB_UNSUPPORTED_ITEM_TYPES, scopeOf, validateActivity };
